import { Injectable } from '@angular/core';
import { Observable, throwError, from } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Auth } from 'aws-amplify';
import { CognitoUser } from '@aws-amplify/auth';

import { UserModel, Credentials } from '@models/index';
import { map, tap, switchMap, catchError, throttleTime } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AppFacadeService } from '@services/app-facade.service';
import { LocationBusinessMetadata } from '@actions/auth/auth.actions';

/**
 * AuthService manages all authentication
 * petitions calling Amplify Service.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private email: string = ""

  constructor(
    private http: HttpClient,
    private appFacadeService: AppFacadeService,
  ) { }

  private auth() {
    return Auth;
  }

  private fromAmplifyResult<T>(result: Promise<T>): Observable<T> {
    //const context = ((new Error().stack || '').split('\n')[1] || '???').split(' ')[0];

    return from(result).pipe(
      catchError(error => {
        // TODO: add remote logging error
        this.appFacadeService.saveCognitoErrors({
          email: this.email,
          path: window.location.href,
          error: JSON.stringify(error),
        })
        return throwError(() => error);
      })
    );
  }

  /**
   * Performs the user log in and
   * gets user authentication tokens
   *
   * @param credentials The user to be
   * logged in credentials. Refer to { @link Credentials }
   *
   * @returns An UserModel Observable
   * that contains the logged in user
   * data.
   */
  login(credentials: Credentials): Observable<UserModel> {
    this.email = credentials.username

    return this.fromAmplifyResult(
      this.auth().signIn(credentials.username, credentials.password)
    ).pipe(
      map(data => data as CognitoUser),
      tap(data => {
        const token = data.getSignInUserSession().getIdToken().getJwtToken()
        localStorage.setItem("token", token)
      }),
      map(data => data["attributes"]),
      map(data => data.email),
      switchMap(email => this.http.get(
        `${environment.apiPath}/users/login?email=${email}&resellerId=${credentials.resellerId}`
      )),
      map(user => user as UserModel)
    )
  }

  /**
   * Performs user sign up via
   * AWS cognito.
   * @param user The user to be logged in
   * data. Refer to { @link UserModel }
   *
   * @returns The signed up user information.
   */
  signUp(user: UserModel): Observable<CognitoUser | any> {
    this.email = user.email

    return this.fromAmplifyResult(
      this.auth().signUp({
        username: user.email,
        password: user.password,
        attributes: {
          email: user.email,
          given_name: user.firstName,
          family_name: user.lastName,
        },
      })
    )
  }

  /**
   * Dispatches a petition to the
   * API to search the user configuration
   * data with a key so the user
   * can sign up.
   * @param payload Contains the unique key
   * to access the user information through
   * the API.
   *
   * @returns An observable of the user that
   * signed up.
   */
  getSignUpUser(payload: { key: string }): Observable<UserModel> {
    return this.http.post(`${environment.apiPath}/users/config`, payload).pipe(
      map((data: { id: string, key: string, user: UserModel }) => data.user)
    );
  }

  /**
   * Performs sign up confirmation
   * service.
   * @returns A boolean observable
   * that contains if the sign up
   * was confirmed or not.
   */
  confirmSignUp({ email, code }): Observable<boolean> {
    this.email = email

    return this.fromAmplifyResult(
      this.auth().confirmSignUp(email, code)
    ).pipe(
      map(() => true)
    )
  }

  /**
   * Performs user log out
   * via AWS cognito.
   * @returns Returns a boolean
   * observable that indicates
   * if the log out process was
   * succesfull or not.
   */
  logout(): Observable<any> {
    console.warn("ENTER---LOGOUT")

    return this.fromAmplifyResult(
      this.auth().signOut()
    ).pipe(
      map(() => true)
    );
  }

  /**
   * Sends a password recovery
   * email to the user via cognito.
   * @param email The user email
   * @returns A boolean  observable
   * that indicates if the email was
   * sent successfully or not.
   */
  sendEmailPasswordRecover(email: string, resellerId: string): Observable<boolean> {
    this.email = email
    return this.http.get<{ result: boolean }>(
      `${environment.apiPath}/users/exist?email=${email}&resellerId=${resellerId}`
    ).pipe(
      switchMap(() =>
        this.fromAmplifyResult(
          this.auth().forgotPassword(email)
        )
      ),
      map(() => true)
    )
  }

  /**
   * Submits the password chage to
   * cognito if a user forgot a password.
   * @param payload Contains the user data including
   * the new password to be set.
   * @returns A boolean observable specifies if
   * the process was successfull or not.
   */
  confirmNewPassword(payload: { username: string, code: string, newPassword: string }): Observable<boolean> {
    this.email = payload.username

    return this.fromAmplifyResult(
      this.auth().forgotPasswordSubmit(payload.username, payload.code, payload.newPassword)
    ).pipe(
      map(() => true)
    )
  }

  /**
   * Submits the user password chage to
   * cognito.
   * @param psw Current user password
   * @param newPsw New user password
   *
   * @returns  A boolean observable specifies if
   * the process was successfull or not.
   */
  changeNewPassword(psw: string, newPsw: string): Observable<boolean> {
    return this.fromAmplifyResult(
      this.auth().currentAuthenticatedUser()
    ).pipe(
      tap((user: string) => this.email = user),
      switchMap(user => this.fromAmplifyResult(
        this.auth().changePassword(user, psw, newPsw)
      )),
      map(() => true)
    )
  }

  checkIfErrorMeansLogout(error: any): boolean {
    return error && (error.message == 'Refresh Token has expired' || error == 'No current user');
  }

  /**
   * Performs a refresh to user session.
   * @returns User refreshed authentication
   * tokens. Note that Authentication token expires
   * every hour, so every hour the session needs
   * to be refresh so the API considers the petitions as
   * valid ones.
   */
  currentSession(): Observable<any> {
    return this.fromAmplifyResult(
      this.auth().currentSession()
    ).pipe(
      throttleTime(2000),
      catchError((error: any) => {
        this.appFacadeService.setErrorTimeInactive(true)
        return throwError(() => error);
      })
    );
  }

  searchLocationAutocompleteOptions$(resellerId?: string): Observable<LocationBusinessMetadata[]> {
    return this.http.get<LocationBusinessMetadata[]>(`${environment.apiPath}/locations/search`, {
      params: resellerId ? { resellerId } : {}
    }).pipe(
      catchError(_error => ([])),
    );
  }
}
